import moment from "moment";

export const formatICSDate = (dateStr) => moment(dateStr).format("YYYYMMDD");

export const formatICSDateTime = (dateStr, timeStr) => {
    if (!timeStr) return formatICSDate(dateStr);
    const timeFormatted = timeStr.length === 4
        ? `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}:00`
        : timeStr;
    return moment(`${dateStr} ${timeFormatted}`, "YYYY-MM-DD HH:mm:ss").format("YYYYMMDDTHHmmss");
};

export const generateICSContent = (session, moduleName, sessionNumber = null, totalSessions = null) => {
    const uid = `${session.session_id || session.date}-${Date.now()}@sozcode.com`;
    const now = moment().format("YYYYMMDDTHHmmss");
    const sessionInfo = sessionNumber && totalSessions ? ` (${sessionNumber}/${totalSessions})` : "";
    const summary = `${moduleName}${sessionInfo}`;
    const locationParts = [session.venue, session.room].filter(Boolean);
    if (session.venueAddress) locationParts.push(session.venueAddress);
    const location = locationParts.join(", ") || "TBC";
    const description = [
        `Module: ${moduleName}`,
        sessionNumber && totalSessions ? `Session: ${sessionNumber} of ${totalSessions}` : null,
        session.venue ? `Venue: ${session.venue}` : null,
        session.venueAddress ? `Address: ${session.venueAddress}` : null,
        session.room ? `Room: ${session.room}` : null
    ].filter(Boolean).join("\\n");

    let dtStart, dtEnd;
    if (session.start_time && session.end_time) {
        dtStart = formatICSDateTime(session.date, session.start_time);
        dtEnd = formatICSDateTime(session.date, session.end_time);
    } else {
        dtStart = formatICSDate(session.date);
        dtEnd = formatICSDate(moment(session.date).add(1, "day").format("YYYY-MM-DD"));
    }

    return [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sozcode LMS//Course Session//EN",
        "CALSCALE:GREGORIAN", "METHOD:PUBLISH", "BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${now}`,
        session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
        session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
        `SUMMARY:${summary}`, `DESCRIPTION:${description}`, `LOCATION:${location}`,
        "STATUS:CONFIRMED", "BEGIN:VALARM", "TRIGGER:-P1D", "ACTION:DISPLAY",
        "DESCRIPTION:Reminder: Training session tomorrow", "END:VALARM",
        "END:VEVENT", "END:VCALENDAR"
    ].join("\r\n");
};

export const generateMultipleICSContent = (sessions, calendarName) => {
    const now = moment().format("YYYYMMDDTHHmmss");
    const totalSessions = sessions.length;
    const events = sessions.map((session, index) => {
        const uid = `${session.session_id || session.date}-${index}-${Date.now()}@sozcode.com`;
        const summary = `${session.moduleName || calendarName} (${index + 1}/${totalSessions})`;
        const locParts = [session.venue, session.room].filter(Boolean);
        if (session.venueAddress) locParts.push(session.venueAddress);
        const location = locParts.join(", ") || "TBC";
        const description = [
            `Course: ${calendarName}`,
            session.moduleName ? `Module: ${session.moduleName}` : null,
            `Session: ${index + 1} of ${totalSessions}`,
            session.venue ? `Venue: ${session.venue}` : null,
            session.venueAddress ? `Address: ${session.venueAddress}` : null,
            session.room ? `Room: ${session.room}` : null
        ].filter(Boolean).join("\\n");

        let dtStart, dtEnd;
        if (session.start_time && session.end_time) {
            dtStart = formatICSDateTime(session.date, session.start_time);
            dtEnd = formatICSDateTime(session.date, session.end_time);
        } else {
            dtStart = formatICSDate(session.date);
            dtEnd = formatICSDate(moment(session.date).add(1, "day").format("YYYY-MM-DD"));
        }

        return [
            "BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${now}`,
            session.start_time ? `DTSTART:${dtStart}` : `DTSTART;VALUE=DATE:${dtStart}`,
            session.start_time ? `DTEND:${dtEnd}` : `DTEND;VALUE=DATE:${dtEnd}`,
            `SUMMARY:${summary}`, `DESCRIPTION:${description}`, `LOCATION:${location}`,
            "STATUS:CONFIRMED", "BEGIN:VALARM", "TRIGGER:-P1D", "ACTION:DISPLAY",
            "DESCRIPTION:Reminder: Training session tomorrow", "END:VALARM", "END:VEVENT"
        ].join("\r\n");
    });
    return [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sozcode LMS//Course Sessions//EN",
        "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
        `X-WR-CALNAME:${calendarName} - Training Sessions`,
        ...events, "END:VCALENDAR"
    ].join("\r\n");
};

export const generateInvoiceDueDatesICS = (invoices, certName, formatCurrencyFn) => {
    const now = moment().format("YYYYMMDDTHHmmss");
    const courseLabel = certName ? certName.trim() : "Course";
    const events = invoices.map((invoice, index) => {
        const due = moment(invoice.due_date).startOf("day");
        const dateStr = due.format("YYYY-MM-DD");
        const dueDateFormatted = due.format("DD MMM YYYY");
        const subtotal = invoice.invoice_line_items?.length > 0
            ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 0), 0) : 0;
        const gst = invoice.gst_amount || 0;
        const total = subtotal + gst;
        const amountStr = formatCurrencyFn(total);
        const lineDesc = invoice.invoice_line_items?.length > 0
            ? invoice.invoice_line_items.map((item) => item.description).filter(Boolean).join(", ") : "";
        const descForSummary = lineDesc || "Invoice";
        const uid = `invoice-due-${invoice.id}-${index}-${Date.now()}@sozcode.com`;
        const summary = `${courseLabel} - Invoice due: ${descForSummary} (${amountStr})`;
        const descriptionParts = [
            `Course/Program: ${courseLabel}`,
            `Amount due: ${amountStr}`,
            `Due date: ${dueDateFormatted}`,
            lineDesc ? `Description: ${lineDesc}` : null,
            "Payment due by the date above.",
        ].filter(Boolean);
        const description = descriptionParts.join("\\n");
        const dtStart = formatICSDate(dateStr);
        const dtEnd = formatICSDate(moment(dateStr).add(1, "day").format("YYYY-MM-DD"));
        const alarmDesc = `${descForSummary} (${amountStr}) for ${courseLabel} due tomorrow`;
        return [
            "BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${now}`,
            `DTSTART;VALUE=DATE:${dtStart}`, `DTEND;VALUE=DATE:${dtEnd}`,
            `SUMMARY:${summary}`, `DESCRIPTION:${description}`, "STATUS:CONFIRMED",
            "BEGIN:VALARM", "TRIGGER:-P1D", "ACTION:DISPLAY",
            `DESCRIPTION:${alarmDesc}`, "END:VALARM", "END:VEVENT"
        ].join("\r\n");
    });
    const calName = certName ? `Invoice due dates - ${certName}` : "Invoice due dates";
    return [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Sozcode LMS//Invoice Due Dates//EN",
        "CALSCALE:GREGORIAN", "METHOD:PUBLISH", `X-WR-CALNAME:${calName}`,
        ...events, "END:VCALENDAR"
    ].join("\r\n");
};

export const downloadICS = (icsContent, filename) => {
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
